import React, { useState, useEffect } from 'react';
import { UniversityQuery, University } from '../../types';
import { useUniversitiesSearch } from '../../services/collegeListService';
import { addUniversityToList, removeUniversityFromList } from '../../services/userUniversityService';
import FilterSidebar from '../../components/FilterSidebar';
import CollegeCard from '../../components/CollegeCard';
import UniversityModal from '../../components/UniversityModal';
import './CollegeListBuilder.css';

const CollegeListBuilder: React.FC = () => {
  const [filters, setFilters] = useState<UniversityQuery>({
    sort_by: 'name',
    order: 'asc',
    page: 1,
    page_size: 20,
  });

  const [allUniversities, setAllUniversities] = useState<University[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const [updatingFavoriteId, setUpdatingFavoriteId] = useState<number | null>(null);
  
  // Modal state
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, isLoading, error, refetch } = useUniversitiesSearch(filters);

  // Debounce search to avoid too many API calls
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  // Update allUniversities when data changes
  useEffect(() => {
    if (data) {
      if (filters.page === 1) {
        // First page or filter change - replace all universities
        setAllUniversities(data.items);
      } else {
        // Load more - preserve scroll position and append to existing universities
        const currentScrollY = window.scrollY;
        setScrollPosition(currentScrollY);
        setAllUniversities(prev => [...prev, ...data.items]);
        
        // Restore scroll position after DOM update
        requestAnimationFrame(() => {
          window.scrollTo(0, currentScrollY);
        });
      }
      setIsLoadingMore(false);
    }
  }, [data, filters.page]);

  useEffect(() => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    const timer = setTimeout(() => {
      refetch();
    }, 300);

    setSearchDebounceTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [filters.search]);

  const handleFiltersChange = (newFilters: UniversityQuery) => {
    // Reset to page 1 and clear universities when filters change
    setAllUniversities([]);
    setFilters({ ...newFilters, page: 1 });
  };

  const handleFavoriteClick = async (universityId: number) => {
    // Prevent multiple clicks on the same university
    if (updatingFavoriteId === universityId) return;

    // Find the university in our current list
    const university = allUniversities.find(u => u.id === universityId);
    if (!university) return;

    try {
      // Set loading state for this specific university
      setUpdatingFavoriteId(universityId);

      // Optimistic update - immediately update the UI
      setAllUniversities(prev => 
        prev.map(u => 
          u.id === universityId 
            ? { ...u, in_my_list: !u.in_my_list }
            : u
        )
      );

      if (university.in_my_list) {
        // Remove from list
        await removeUniversityFromList(universityId);
      } else {
        // Add to list
        await addUniversityToList(universityId);
      }

      // Refetch data to ensure consistency with backend
      refetch();
    } catch (error) {
      // Revert optimistic update on error
      setAllUniversities(prev => 
        prev.map(u => 
          u.id === universityId 
            ? { ...u, in_my_list: university.in_my_list }
            : u
        )
      );
      
      console.error('Failed to update university list:', error);
      // You could show a toast notification here
    } finally {
      // Clear loading state
      setUpdatingFavoriteId(null);
    }
  };

  const handleLoadMore = () => {
    if (data?.has_next && !isLoadingMore) {
      setIsLoadingMore(true);
      setFilters(prev => ({ ...prev, page: (prev.page || 1) + 1 }));
    }
  };

  // Modal handlers
  const handleUniversityClick = (university: University) => {
    setSelectedUniversity(university);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedUniversity(null);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#fafbfc] flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl text-red-600 mb-4">Error loading universities</div>
          <button 
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="college-list-container">
      <div className="main-content">
        {/* Sidebar Filters */}
        <div data-tour="filter-panel">
          <FilterSidebar filters={filters} onFiltersChange={handleFiltersChange} />
        </div>

        {/* Main Results */}
        <div className="results-section">
          {isLoading ? (
            <div className="flex flex-col space-y-6">
              {/* Loading Header */}
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-48"></div>
              </div>
              
              {/* Loading Cards */}
              {[...Array(6)].map((_, index) => (
                <div key={index} className="border rounded-xl flex p-6 items-center space-x-6 animate-pulse" style={{ background: 'var(--surface-primary)', borderColor: 'var(--border-primary)' }}>
                  {/* Image Placeholder */}
                  <div className="rounded-lg w-[120px] h-[90px] bg-gray-200"></div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-4">
                      <div className="space-y-2">
                        <div className="h-6 bg-gray-200 rounded w-64"></div>
                        <div className="h-4 bg-gray-200 rounded w-48"></div>
                      </div>
                      <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
                    </div>
                    
                    <div className="h-6 bg-gray-200 rounded w-16 mb-4"></div>
                    
                    <div className="flex items-center space-x-12">
                      <div className="space-y-1">
                        <div className="h-8 bg-gray-200 rounded w-12"></div>
                        <div className="h-3 bg-gray-200 rounded w-16"></div>
                      </div>
                      <div className="space-y-1">
                        <div className="h-8 bg-gray-200 rounded w-8"></div>
                        <div className="h-3 bg-gray-200 rounded w-16"></div>
                      </div>
                      <div className="space-y-1">
                        <div className="h-8 bg-gray-200 rounded w-10"></div>
                        <div className="h-3 bg-gray-200 rounded w-20"></div>
                      </div>
                      <div className="ml-auto">
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="animate-fadeIn">
              {/* Results Header */}
              {data && (
                <div className="text-sm text-gray-600 transition-opacity duration-300">
                  Showing {allUniversities.length} of {data.total} universities
                  {filters.search && ` for "${filters.search}"`}
                </div>
              )}

              {/* University Cards */}
              <div className="space-y-6">
                {allUniversities.map((university, index) => (
                  <div 
                    key={university.id}
                    className="animate-slideInUp"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <CollegeCard
                      university={university}
                      onFavoriteClick={handleFavoriteClick}
                      isUpdatingFavorite={updatingFavoriteId === university.id}
                      onClick={() => handleUniversityClick(university)}
                    />
                  </div>
                ))}
              </div>
              {/* Load More Button */}
              {data && data.has_next && (
                <div className="load-more-container animate-fadeIn">
                  <button
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    className="load-more-button"
                  >
                    {isLoadingMore ? (
                      <div className="loading-spinner">
                        <div className="spinner"></div>
                        <span>Loading...</span>
                      </div>
                    ) : (
                      'Load More'
                    )}
                  </button>
                </div>
              )}

              {/* No Results */}
              {data && allUniversities.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-xl text-gray-600 mb-2">No universities found</div>
                  <div className="text-sm text-gray-500">Try adjusting your filters</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* University Details Modal */}
      <UniversityModal
        university={selectedUniversity}
        isOpen={isModalOpen}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default CollegeListBuilder;
